import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../api/config';
import { PageShell } from '../components/Layout';
import { IconFile, IconSearch } from '../components/Icons';

export default function HomePage() {
  const navigate = useNavigate();
  const [locationIndex, setLocationIndex] = useState(null);
  const [selectedDistrictId, setSelectedDistrictId] = useState(0);
  const [selectedTalukaId, setSelectedTalukaId] = useState(0);
  const [selectedVillageId, setSelectedVillageId] = useState(0);
  const [query, setQuery] = useState('');
  const [propertyType, setPropertyType] = useState('land');
  const [step, setStep] = useState(1);

  useEffect(() => {
    fetch(`${API_BASE_URL}/locations`)
      .then((res) => res.json())
      .then((data) => {
        if (!data.districts) return;
        setLocationIndex(data);
        const pune = data.districts.find((d) => d.mr === 'पुणे' || d.en === 'Pune');
        const d0 = pune || data.districts[0];
        if (!d0) return;
        setSelectedDistrictId(d0.id);
        const t0 = d0.talukas?.[0];
        if (t0) {
          setSelectedTalukaId(t0.id);
          const v0 = t0.villages?.[0];
          if (v0) setSelectedVillageId(v0.id);
        }
      })
      .catch(console.error);
  }, []);

  const districtRow = locationIndex?.districts?.find((d) => d.id === selectedDistrictId) || null;
  const talukaList = districtRow?.talukas || [];
  const talukaRow = talukaList.find((t) => t.id === selectedTalukaId);
  const villageList = talukaRow?.villages || [];
  const villageRow = villageList.find((v) => v.id === selectedVillageId);

  const handleQueryChange = (e) => {
    const val = e.target.value;
    if (val === '' || /^[0-9\b]+$/.test(val)) setQuery(val);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!districtRow || !talukaRow || !villageRow || !query) {
      alert('Please complete all fields.');
      return;
    }
    const params = new URLSearchParams({
      d: String(districtRow.id),
      t: String(talukaRow.id),
      v: String(villageRow.id),
      de: districtRow.en,
      te: talukaRow.en,
      ve: villageRow.en,
      query,
      propertyType,
    });
    navigate(`/results?${params.toString()}`);
  };

  const canStep2 = selectedDistrictId > 0 && selectedTalukaId > 0 && selectedVillageId > 0;
  const canStep3 = canStep2 && query.length > 0;

  return (
    <PageShell>
      <section className="pattern-grid border-b-2 border-sand">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-20 grid lg:grid-cols-2 gap-12 items-start">
          <div className="lg:pt-8">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-clay mb-4">
              Maharashtra · IGR Records
            </p>
            <h1 className="page-title leading-[1.15]">
              Search property
              <span className="block text-clay italic">sale &amp; transfer</span>
              records
            </h1>
            <p className="mt-6 text-ink/70 text-lg leading-relaxed max-w-md">
              Look up registered documents by location and plot number. Pay once, receive a full HTML report with document details.
            </p>

            <ul className="mt-10 space-y-4">
              {[
                { icon: IconSearch, text: 'Search by district, taluka, village & property number' },
                { icon: IconFile, text: 'Preview matched records before purchase' },
              ].map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-start gap-3 text-sm font-medium text-ink/80">
                  <span className="mt-0.5 w-8 h-8 shrink-0 border-2 border-forest flex items-center justify-center text-forest">
                    <Icon className="w-4 h-4" />
                  </span>
                  {text}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white border-2 border-forest shadow-stamp">
            <div className="border-b-2 border-sand px-6 py-4 flex items-center gap-3">
              {[1, 2, 3].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => {
                    if (n === 2 && !canStep2) return;
                    if (n === 3 && !canStep3) return;
                    setStep(n);
                  }}
                  className={`flex-1 text-center py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${
                    step === n
                      ? 'border-clay text-clay'
                      : 'border-transparent text-ink/40 hover:text-ink/60'
                  }`}
                >
                  Step {n}
                </button>
              ))}
            </div>

            <form onSubmit={handleSearch} className="p-6 md:p-8 space-y-5">
              {step === 1 && (
                <>
                  <p className="text-sm text-ink/60 font-medium">Select location hierarchy</p>
                  <div>
                    <label className="field-label">District</label>
                    <select
                      className="field-input"
                      value={selectedDistrictId || ''}
                      onChange={(e) => {
                        const id = parseInt(e.target.value, 10);
                        setSelectedDistrictId(id);
                        const dr = locationIndex?.districts?.find((x) => x.id === id);
                        const t1 = dr?.talukas?.[0];
                        setSelectedTalukaId(t1?.id || 0);
                        setSelectedVillageId(t1?.villages?.[0]?.id || 0);
                      }}
                      required
                    >
                      <option value="">Choose district</option>
                      {(locationIndex?.districts || []).map((d) => (
                        <option key={d.id} value={d.id}>{d.en}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="field-label">Taluka</label>
                      <select
                        className="field-input"
                        value={selectedTalukaId || ''}
                        onChange={(e) => {
                          const id = parseInt(e.target.value, 10);
                          setSelectedTalukaId(id);
                          const tr = talukaList.find((x) => x.id === id);
                          setSelectedVillageId(tr?.villages?.[0]?.id || 0);
                        }}
                        disabled={!districtRow}
                        required
                      >
                        <option value="">Taluka</option>
                        {talukaList.map((t) => (
                          <option key={t.id} value={t.id}>{t.en}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="field-label">Village</label>
                      <select
                        className="field-input"
                        value={selectedVillageId || ''}
                        onChange={(e) => setSelectedVillageId(parseInt(e.target.value, 10))}
                        disabled={!talukaRow}
                        required
                      >
                        <option value="">Village</option>
                        {villageList.map((v) => (
                          <option key={v.id} value={v.id}>{v.en}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={!canStep2}
                    onClick={() => setStep(2)}
                    className="btn-primary w-full disabled:opacity-40"
                  >
                    Next — Property details
                  </button>
                </>
              )}

              {step === 2 && (
                <>
                  <p className="text-sm text-ink/60 font-medium">Enter property identifier</p>
                  <div>
                    <label className="field-label">Property type</label>
                    <select
                      className="field-input"
                      value={propertyType}
                      onChange={(e) => setPropertyType(e.target.value)}
                    >
                      <option value="land">Plot / Survey / CTS / Gut</option>
                      <option value="flat">Flat / Shop / Office</option>
                    </select>
                  </div>
                  <div>
                    <label className="field-label">Property number</label>
                    <input
                      className="field-input text-lg font-semibold"
                      placeholder="e.g. 128"
                      required
                      value={query}
                      onChange={handleQueryChange}
                    />
                  </div>
                  <div className="flex gap-3">
                    <button type="button" onClick={() => setStep(1)} className="btn-outline flex-1">
                      Back
                    </button>
                    <button
                      type="button"
                      disabled={!canStep3}
                      onClick={() => setStep(3)}
                      className="btn-primary flex-1 disabled:opacity-40"
                    >
                      Review
                    </button>
                  </div>
                </>
              )}

              {step === 3 && (
                <>
                  <div className="bg-parchment border border-sand p-4 text-sm space-y-2">
                    <p><span className="font-semibold text-forest">District:</span> {districtRow?.en}</p>
                    <p><span className="font-semibold text-forest">Taluka:</span> {talukaRow?.en}</p>
                    <p><span className="font-semibold text-forest">Village:</span> {villageRow?.en}</p>
                    <p><span className="font-semibold text-forest">Property:</span> {query} ({propertyType === 'land' ? 'Land' : 'Flat/Unit'})</p>
                  </div>
                  <label className="flex items-start gap-3 text-xs text-ink/60 cursor-pointer">
                    <input type="checkbox" required className="mt-0.5 rounded border-sand text-forest focus:ring-forest" />
                    I confirm this search is for lawful purposes and accept the terms of use.
                  </label>
                  <div className="flex gap-3">
                    <button type="button" onClick={() => setStep(2)} className="btn-outline flex-1">
                      Back
                    </button>
                    <button type="submit" className="btn-primary flex-1">
                      <IconSearch className="w-5 h-5" />
                      Search records
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
